import { api } from '@connected/api-client';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { ContentLayout } from '@connected/ui/layouts/content/layout';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, KeyRound, Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

type FormValues = {
    email: string;
};

export const Route = createFileRoute('/_unauth/forgot-password/')({
    component: Page,
});

const DEFAULT_VALUES: FormValues = {
    email: '',
};

function Page() {
    const { t } = useTranslation('unauth');
    const [postSuccess, setPostSuccess] = useState(false);

    const mutation = api.useMutation('post', '/api/v2/accounts/passwords/forgot', {
        onSuccess: async () => {
            setPostSuccess(true);
        },
        onError: ({ Message }) => {
            return toast.error(Message);
        },
    });

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: ({ value }) => mutation.mutate({
            body: {
                Email: value.email,
            },
        }),
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <ContentLayout className="h-full w-full items-center gap-0 justify-center max-w-none">
            <ContentLayout.Header className="w-full max-w-sm">
                <div className="flex justify-center">
                    <div className="border border-solid rounded-xl w-fit p-3 m-3">
                        {!postSuccess ? <KeyRound /> : <Mail />}
                    </div>
                </div>
                <ContentLayout.Title className="text-center text-3xl">
                    {!postSuccess
                        ? t('forgot-password.pending.title')
                        : t('forgot-password.success.title')}
                </ContentLayout.Title>
                <ContentLayout.SubTitle className="text-center text-base font-normal px-3 ">
                    {!postSuccess
                        ? t('forgot-password.pending.description')
                        : (
                                <>
                                    {t('forgot-password.success.description')}
                                    <p className="font-bold">
                                        {form.getFieldValue('email')}
                                    </p>
                                </>
                            )}
                </ContentLayout.SubTitle>
            </ContentLayout.Header>
            <form className="w-full max-w-sm flex flex-col gap-4 p-3" onSubmit={handleSubmit}>
                <form.AppForm>
                    {!postSuccess && (
                        <>
                            <form.AppField
                                name="email"
                                validators={{
                                    onSubmit: z
                                        .string()
                                        .email(t('forgot-password.fields.email.error-invalid-email')),
                                }}
                            >
                                {field => (
                                    <field.Label>
                                        <field.Label.Title label={t('forgot-password.fields.email.title')} />
                                        <field.Input
                                            placeholder={t('forgot-password.fields.email.placeholder')}
                                            type="email"
                                            autoComplete="email"
                                            autoFocus
                                        />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            </form.AppField>
                        </>
                    )}

                    {!postSuccess
                        && (
                            <form.SubmitButton asChild>
                                <Button
                                    type="submit"
                                    className="mt-2 text-base"
                                    disabled={mutation.isPending}
                                >
                                    {t('forgot-password.pending.button')}
                                </Button>
                            </form.SubmitButton>
                        )}

                    {mutation.isError && (
                        <em>{JSON.stringify(mutation.error, null, 2)}</em>
                    )}
                    <div className="flex flex-col">
                        {postSuccess && (
                            <>
                                <span className="flex flex-col gap-2 items-center justify-center text-sm mt-1">
                                    <form.SubmitButton asChild>
                                        <Button
                                            type="submit"
                                            form="forgotPasswordForm"
                                            variant="link"
                                            className="font-semibold"
                                            disabled={mutation.isPending}
                                        >
                                            {t('forgot-password.success.email.resend')}
                                        </Button>
                                    </form.SubmitButton>
                                </span>
                            </>
                        )}
                        <Button
                            variant="link"
                            className="font-semibold hover:no-underline text-foreground"
                            asChild
                        >
                            <Link to="/login">
                                <ArrowLeft />
                                {' '}
                                {t('forgot-password.back-to-login')}
                            </Link>
                        </Button>
                    </div>
                </form.AppForm>
            </form>
        </ContentLayout>
    );
}
